import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { api } from '../utils/api';
import { track } from '../utils/observability';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { useUnsavedGuard } from '../contexts/UnsavedGuardContext';
import { LimitReachedScreen } from '../components/LimitReachedScreen';
import { EmailVerificationGate } from '../components/EmailVerificationGate';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Mic, MicOff, Upload, FileText, CheckCircle, ArrowRight, ArrowLeft,
  ClipboardCheck, TrendingUp, Activity, User as UserIcon, Loader2,
  Play, Pause, Square, AlertCircle, Edit, Trash2, Save, X, Check,
  ChevronDown, ChevronUp, Download
} from 'lucide-react';

// ==================== STEP PROGRESS BAR ====================
const StepProgressBar = ({ currentStep, consultationType, templateType }) => {
  const specialtyLabel = {
    pilates: 'Pilates',
    pilates_clinico: 'Pilates Clínico',
    consultorio: 'Fisioterapia Clínica'
  }[templateType] || null;

  const steps = [
    { num: 2, label: 'Especialidade' },
    { num: 3, label: 'Tipo' },
    { num: 5, label: 'Gravação' },
    { num: 6, label: 'IA Processa' },
    { num: 7, label: 'Revisão' },
    { num: 8, label: 'Aprovação' },
    { num: 9, label: 'Salvar' }
  ];

  return (
    <div className="mb-6">
      {/* Current context label */}
      {(specialtyLabel || consultationType) && (
        <div className="text-center mb-3">
          <Badge variant="outline" className="text-sm">
            {specialtyLabel ? `${specialtyLabel}` : ''}
            {specialtyLabel && consultationType ? ' · ' : ''}
            {consultationType ? (consultationType === 'avaliacao' ? 'Avaliação' : 'Evolução') : ''}
          </Badge>
        </div>
      )}
      
      {/* Progress bar */}
      <div className="flex items-center justify-between max-w-2xl mx-auto">
        {steps.map((step, index) => (
          <React.Fragment key={step.num}>
            <div className="flex flex-col items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  currentStep >= step.num 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                } ${currentStep === step.num ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
              >
                {currentStep > step.num ? <Check className="w-4 h-4" /> : index + 1}
              </div>
              <span className={`text-xs mt-1 ${currentStep >= step.num ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${currentStep > step.num ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ==================== STEP 2 (NEW): SPECIALTY SELECTION ====================
const SpecialtySelection = ({ patient, onSelect }) => {
  const specialties = [
    {
      key: 'pilates',
      title: 'Pilates',
      description: 'Para aulas e sessões de condicionamento. Gera: registro da sessão, objetivos trabalhados, progressão e observações.',
      icon: Activity,
      // Static Tailwind classes (avoid dynamic class generation that gets purged)
      iconBg: 'bg-purple-100 dark:bg-purple-500/20',
      iconColor: 'text-purple-600 dark:text-purple-300',
      hover: 'hover:border-purple-500',
    },
    {
      key: 'pilates_clinico',
      title: 'Pilates Clínico',
      description: 'Para reabilitação com método Pilates. Gera: anamnese, avaliação funcional, diagnóstico fisioterapêutico e plano de tratamento.',
      icon: ClipboardCheck,
      iconBg: 'bg-teal-100 dark:bg-teal-500/20',
      iconColor: 'text-teal-600 dark:text-teal-300',
      hover: 'hover:border-teal-500',
    },
    {
      key: 'consultorio',
      title: 'Fisioterapia Clínica',
      description: 'Para consultas de fisioterapia clínica. Gera: prontuário completo COFFITO 414/2012 com CIF, diagnóstico funcional e plano terapêutico.',
      icon: FileText,
      iconBg: 'bg-blue-100 dark:bg-blue-500/20',
      iconColor: 'text-blue-600 dark:text-blue-300',
      hover: 'hover:border-blue-500',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">Qual a especialidade desta consulta?</h2>
        <p className="text-gray-500">Selecione a modalidade para {patient?.name}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {specialties.map((s) => {
          const Icon = s.icon;
          return (
            <Card
              key={s.key}
              className={`cursor-pointer hover:shadow-lg transition-all ${s.hover}`}
              onClick={() => onSelect(s.key)}
              data-testid={`specialty-${s.key}`}
            >
              <CardContent className="p-8 text-center">
                <div className={`w-16 h-16 ${s.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Icon className={`w-8 h-8 ${s.iconColor}`} strokeWidth={2} />
                </div>
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{s.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

// ==================== STEP 3: CONSULTATION TYPE SELECTION ====================
const ConsultationTypeSelection = ({ patient, onSelect, hasApprovedEvaluation, approvedEvaluationDate, onBack }) => {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-specialty">
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
      </div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">Tipo de Consulta</h2>
        <p className="text-gray-500">
          {hasApprovedEvaluation
            ? `Esta consulta para ${patient?.name} é Avaliativa ou Evolutiva?`
            : `Selecione o tipo de atendimento para ${patient?.name}`}
        </p>
      </div>

      {/* Positive confirmation when an approved evaluation exists */}
      {hasApprovedEvaluation && (
        <div
          className="max-w-3xl mx-auto mb-6 p-4 rounded-lg border-l-4 border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-400 flex items-start gap-3"
          data-testid="has-evaluation-banner"
        >
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-300 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-emerald-800 dark:text-emerald-200">
            Este paciente já possui <strong>avaliação aprovada</strong>
            {approvedEvaluationDate ? (
              <> em {new Date(approvedEvaluationDate).toLocaleDateString('pt-BR')}</>
            ) : null}.
            Você pode registrar uma evolução ou iniciar uma reavaliação.
          </p>
        </div>
      )}

      {/* Alert ONLY when there is NO approved evaluation */}
      {!hasApprovedEvaluation && (
        <div
          className="max-w-3xl mx-auto mb-6 p-4 rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-400 flex items-start gap-3"
          data-testid="no-evaluation-alert"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-300 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <strong>⚠️ Atenção:</strong> A avaliação inicial ainda não foi realizada.
            Recomendamos realizá-la o quanto antes para garantir a qualidade do
            acompanhamento clínico. Você ainda pode registrar uma Evolução simples
            com o relato da consulta.
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card 
          className="cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all"
          onClick={() => onSelect('avaliacao')}
          data-testid="consultation-type-avaliacao"
        >
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardCheck className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Consulta Avaliativa</h3>
            <p className="text-gray-500 text-sm mb-3">
              {hasApprovedEvaluation
                ? 'Reavaliação completa do paciente. Substitui a avaliação anterior.'
                : 'Primeira consulta. Gera anamnese, diagnóstico funcional, objetivos e plano de tratamento.'}
            </p>
            <Badge variant="secondary" className="text-xs">
              Gravação: até 30 minutos
            </Badge>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:border-green-500 hover:shadow-lg transition-all"
          onClick={() => onSelect('evolucao')}
          data-testid="consultation-type-evolucao"
        >
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Consulta Evolutiva</h3>
            <p className="text-gray-500 text-sm mb-3">
              {hasApprovedEvaluation
                ? 'Registro de sessão de retorno. Evolução clínica baseada no histórico.'
                : 'Relato rápido da sessão quando a avaliação ainda não foi feita.'}
            </p>
            <Badge variant="secondary" className="text-xs">
              Gravação: até 5 minutos
            </Badge>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== STEP 4: TEMPLATE SELECTION ====================
const TemplateSelection = ({ consultationType, onSelect, onBack }) => {
  // Recording time is based on consultation TYPE, not template
  const recordingTimeText = consultationType === 'avaliacao' 
    ? 'até 30 minutos' 
    : 'até 5 minutos';

  // Label for Evolução varies by template context
  const getEvolutionLabel = (template) => {
    if (consultationType === 'evolucao') {
      return template === 'consultorio' 
        ? 'Evolução de Consulta / Sessão' 
        : 'Evolução de Aula / Sessão';
    }
    return consultationType === 'avaliacao' ? 'Avaliação' : 'Evolução';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <h2 className="text-2xl font-bold mb-2">Escolha o Template</h2>
        <p className="text-gray-500">
          {consultationType === 'avaliacao' ? 'Avaliação' : 'Evolução'} - Selecione o modelo de documentação
        </p>
        <Badge variant="secondary" className="mt-2">
          Tempo de gravação: {recordingTimeText}
        </Badge>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* CONSULTÓRIO / FISIOTERAPIA CLÍNICA Card */}
        <Card 
          className="cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all"
          onClick={() => onSelect('consultorio')}
          data-testid="template-consultorio"
        >
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Fisioterapia Clínica</h3>
            {consultationType === 'evolucao' && (
              <Badge variant="outline" className="mb-3 text-blue-600">
                {getEvolutionLabel('consultorio')}
              </Badge>
            )}
            <p className="text-gray-500 text-sm mb-4">
              {consultationType === 'avaliacao' 
                ? 'Prontuário completo com EVA, amplitude de movimento, testes funcionais, diagnóstico cinético-funcional e plano de tratamento.'
                : 'Registro da consulta ou sessão de retorno: apresentação do paciente, procedimentos realizados, evolução clínica e conduta para próxima sessão.'}
            </p>
          </CardContent>
        </Card>

        {/* PILATES Card */}
        <Card 
          className="cursor-pointer hover:border-purple-500 hover:shadow-lg transition-all"
          onClick={() => onSelect('pilates')}
          data-testid="template-pilates"
        >
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Pilates</h3>
            {consultationType === 'evolucao' && (
              <Badge variant="outline" className="mb-3 text-purple-600">
                {getEvolutionLabel('pilates')}
              </Badge>
            )}
            <p className="text-gray-500 text-sm mb-4">
              {consultationType === 'avaliacao'
                ? 'Avaliação inicial do aluno com histórico de saúde, avaliação postural, objetivos e proposta inicial de trabalho.'
                : 'Registro da aula ou sessão: queixas iniciais, exercícios realizados, intercorrências, resposta do aluno e proposta para próxima aula.'}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== STEP 5: RECORDING/INPUT ====================
// Helper: converts any axios/backend error into a friendly pt-BR string.
// Avoids "[object Object]" when FastAPI returns a 422 with detail=[{...}].
const formatTranscribeError = (err) => {
  // Network failure (no response received)
  if (err && !err.response) {
    if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
      return 'A transcrição demorou demais. Verifique sua conexão e tente novamente.';
    }
    if (/network/i.test(err.message || '')) {
      return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }
  }

  const status = err?.response?.status;
  const data = err?.response?.data;

  // Map common HTTP statuses to user-friendly messages
  if (status === 401 || status === 403) {
    return 'Sua sessão expirou. Faça login novamente para continuar.';
  }
  if (status === 413) {
    return 'O áudio é grande demais (limite 25 MB). Grave um trecho menor.';
  }
  if (status === 429) {
    return 'Muitas tentativas em pouco tempo. Aguarde alguns segundos e tente novamente.';
  }
  if (status === 503 || status === 502 || status === 504) {
    return 'Serviço de transcrição temporariamente indisponível. Tente novamente em instantes.';
  }

  // Try to extract a readable detail string from FastAPI / our backend
  let detailText = '';
  if (data) {
    if (typeof data === 'string') {
      detailText = data;
    } else if (typeof data.detail === 'string') {
      detailText = data.detail;
    } else if (Array.isArray(data.detail)) {
      // FastAPI Pydantic validation: pick the first msg
      detailText = data.detail
        .map(d => (typeof d === 'string' ? d : (d?.msg || '')))
        .filter(Boolean)
        .join('; ');
    } else if (typeof data.message === 'string') {
      detailText = data.message;
    } else if (typeof data.error === 'string') {
      detailText = data.error;
    }
  }

  // Specific pt-BR mappings based on backend keywords
  if (detailText) {
    if (/empty|vazio/i.test(detailText)) {
      return 'O áudio gravado está vazio. Grave novamente e fale próximo ao microfone.';
    }
    if (/format|formato/i.test(detailText)) {
      return 'Formato de áudio não suportado. Tente gravar novamente em outro navegador.';
    }
    if (/whisper|groq|api key|unauthor/i.test(detailText)) {
      return 'Serviço de transcrição indisponível no momento. Tente novamente em instantes.';
    }
    return `Erro ao transcrever: ${detailText}`;
  }

  // Final fallbacks (NEVER show "[object Object]")
  if (err?.message && typeof err.message === 'string') {
    return `Erro ao transcrever: ${err.message}`;
  }
  return 'Erro ao transcrever o áudio. Tente novamente ou digite manualmente.';
};

const RecordingStep = ({ 
  consultationType, 
  templateType, 
  onTranscriptionComplete, 
  onBack,
  onFunnelEvent
}) => {
  const [inputMode, setInputMode] = useState(null); // 'record' or 'type'
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const recordedMimeTypeRef = useRef('audio/webm');

  // Max recording time: 5 min (300s) for evolução, 30 min (1800s) for avaliação
  const maxRecordingSeconds = consultationType === 'evolucao' ? 300 : 1800;
  const remainingSeconds = Math.max(0, maxRecordingSeconds - recordingTime);
  const isEvolucao = consultationType === 'evolucao';

  // Cleanup: garante que stream e timer sejam liberados em qualquer caminho
  const cleanupStream = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(t => {
          try { t.stop(); } catch (_) { /* no-op */ }
        });
      } catch (_) { /* no-op */ }
      streamRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupStream();
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch (_) { /* no-op */ }
    };
  }, [cleanupStream]);

  // Negocia o mimeType disponível: opus → webm → mp4 → padrão do navegador
  const pickSupportedMimeType = () => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ];
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
      return null;
    }
    if (typeof window.MediaRecorder.isTypeSupported !== 'function') {
      return ''; // sem checagem disponível, deixa o navegador escolher
    }
    for (const mt of candidates) {
      try {
        if (window.MediaRecorder.isTypeSupported(mt)) return mt;
      } catch (_) { /* try next */ }
    }
    return ''; // padrão do navegador
  };

  const startRecording = async () => {
    setError(null);

    // Pré-checagens defensivas para evitar tela preta / promessa que nunca resolve
    if (typeof window === 'undefined' || !window.isSecureContext) {
      setError('A gravação só funciona em conexão segura (HTTPS). Acesse pelo domínio oficial e tente novamente.');
      return;
    }
    if (!navigator?.mediaDevices?.getUserMedia) {
      setError('Seu navegador não suporta gravação de áudio. Use o Chrome (Android/Desktop) ou Safari atualizado (iOS).');
      return;
    }
    if (typeof window.MediaRecorder === 'undefined') {
      setError('Seu navegador não suporta gravação de áudio (MediaRecorder indisponível). Atualize o navegador.');
      return;
    }

    // HOLD MODEL — nunca deixar gravar áudio sem crédito disponível
    try {
      const bal = await api.getCreditsBalance();
      if ((bal?.availableTotal ?? 1) < 1) {
        setError('Você não tem créditos disponíveis para gerar um novo prontuário. Adquira créditos ou aguarde a renovação do seu plano.');
        return;
      }
    } catch (_) { /* falha na checagem não deve bloquear a gravação */ }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err?.name || '';
      let msg = 'Não foi possível acessar o microfone. Verifique as permissões do navegador.';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        msg = 'Acesso ao microfone negado. Toque no cadeado da barra de endereço, autorize o microfone e tente novamente.';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        msg = 'Nenhum microfone encontrado. Conecte um microfone (ou habilite o microfone do dispositivo) e tente novamente.';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        msg = 'O microfone está sendo usado por outro aplicativo. Feche outras chamadas/gravações e tente novamente.';
      } else if (name === 'SecurityError') {
        msg = 'A gravação foi bloqueada por motivos de segurança. Acesse via HTTPS e tente novamente.';
      } else if (name === 'AbortError') {
        msg = 'A solicitação de microfone foi cancelada. Tente novamente.';
      }
      setError(msg);
      return;
    }

    streamRef.current = stream;

    // Cria o MediaRecorder com mimeType negociado
    let recorder;
    try {
      const mimeType = pickSupportedMimeType();
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recordedMimeTypeRef.current = recorder.mimeType || mimeType || 'audio/webm';
    } catch (err) {
      cleanupStream();
      setError('Não foi possível iniciar a gravação neste navegador. Tente atualizar o navegador ou use a opção "Digitar manualmente".');
      return;
    }

    mediaRecorderRef.current = recorder;
    audioChunksRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    recorder.onerror = () => {
      cleanupStream();
      setIsRecording(false);
      setError('Erro inesperado durante a gravação. Tente novamente ou use a opção "Digitar manualmente".');
    };

    recorder.onstop = () => {
      try {
        const type = recordedMimeTypeRef.current || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type });
        if (blob.size > 0) {
          setAudioBlob(blob);
        } else {
          setError('A gravação ficou vazia. Verifique o microfone e tente novamente.');
        }
      } catch (_) {
        setError('Não foi possível processar a gravação. Tente novamente.');
      } finally {
        cleanupStream();
      }
    };

    try {
      recorder.start(1000);
    } catch (err) {
      cleanupStream();
      setError('Não foi possível iniciar a gravação. Tente novamente.');
      return;
    }

    setIsRecording(true);
    setRecordingTime(0);

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const next = prev + 1;
        if (next >= maxRecordingSeconds) {
          try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
              mediaRecorderRef.current.stop();
            }
          } catch (_) { /* no-op */ }
          setIsRecording(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          if (isEvolucao) {
            setError('Tempo máximo de 5 minutos atingido. A gravação foi encerrada automaticamente.');
          }
        }
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (_) { /* no-op */ }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const cancelRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        // Substitui onstop temporariamente para descartar os chunks
        mediaRecorderRef.current.onstop = () => { cleanupStream(); };
        mediaRecorderRef.current.stop();
      }
    } catch (_) { /* no-op */ }
    audioChunksRef.current = [];
    cleanupStream();
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setError(null);
    setInputMode(null);
  };

  const handleTranscribe = async () => {
    if (!audioBlob) return;

    setIsTranscribing(true);
    setError(null);

    try {
      const formData = new FormData();
      const mt = recordedMimeTypeRef.current || 'audio/webm';
      const ext = mt.includes('mp4') ? 'm4a'
                : mt.includes('ogg') ? 'ogg'
                : mt.includes('mpeg') ? 'mp3'
                : 'webm';
      formData.append('file', audioBlob, `recording.${ext}`);

      const result = await api.transcribeAudio(formData);
      const text = result?.transcription || result?.text || '';

      if (!text || !text.trim()) {
        setError('Não conseguimos detectar fala no áudio. Verifique o microfone e tente gravar novamente.');
        return;
      }

      setTranscription(text);
      // Funnel event #2 — audio_recorded (fires after audio transcription completes)
      try { onFunnelEvent && onFunnelEvent('audio_recorded', { input_mode: 'audio' }); } catch (_) { /* ignore */ }
    } catch (err) {
      // Sentry breadcrumb: full context for field debugging without reproduction
      try {
        Sentry.addBreadcrumb({
          category: 'transcription',
          message: 'Transcription request failed',
          level: 'error',
          data: {
            httpStatus: err?.status || err?.response?.status || null,
            blobSize: audioBlob?.size ?? null,
            mimeType: audioBlob?.type || recordedMimeTypeRef.current || null,
            recordingSeconds: recordingTime,
            errorMessage: err?.message || (typeof err === 'string' ? err : JSON.stringify(err?.response?.data || err)),
          },
        });
        Sentry.captureException(err, {
          tags: { feature: 'transcription' },
          extra: {
            httpStatus: err?.response?.status,
            blobSize: audioBlob?.size,
            mimeType: audioBlob?.type,
            recordingSeconds: recordingTime,
          },
        });
      } catch (_) { /* never let observability break UX */ }

      setError(formatTranscribeError(err));
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleContinue = () => {
    if (transcription.trim()) {
      // Funnel event #2 (manual-typing path) — audio_recorded equivalent when user
      // finishes the "input" stage via manual entry. Only fire if NOT coming from
      // an audio blob (audio path already fired in transcribeAudio).
      if (!audioBlob) {
        try { onFunnelEvent && onFunnelEvent('audio_recorded', { input_mode: 'manual' }); } catch (_) { /* ignore */ }
      }
      onTranscriptionComplete(transcription);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <h2 className="text-2xl font-bold mb-2">Registrar Consulta</h2>
        <p className="text-gray-500">Grave ou digite as informações da sessão</p>
      </div>

      {!inputMode && (
        <div className="grid md:grid-cols-2 gap-6">
          <Card 
            className="cursor-pointer hover:border-blue-500 transition-all"
            onClick={() => setInputMode('record')}
            data-testid="input-mode-record"
          >
            <CardContent className="p-8 text-center">
              <Mic className="w-12 h-12 mx-auto mb-4 text-blue-500" />
              <h3 className="font-bold mb-2">Gravar Áudio</h3>
              <p className="text-sm text-gray-500">
                Grave a consulta e deixe a IA transcrever automaticamente
              </p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:border-green-500 transition-all"
            onClick={() => setInputMode('type')}
            data-testid="input-mode-type"
          >
            <CardContent className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-green-500" />
              <h3 className="font-bold mb-2">Digitar Manualmente</h3>
              <p className="text-sm text-gray-500">
                Digite as informações da consulta diretamente
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {inputMode === 'record' && (
        <Card>
          <CardContent className="p-8">
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}
            
            {!audioBlob ? (
              <div className="text-center">
                <div className={`w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center transition-all ${
                  isRecording ? 'bg-red-100 animate-pulse' : 'bg-gray-100'
                }`}>
                  {isRecording ? (
                    <div className="text-center">
                      <MicOff className="w-12 h-12 text-red-500 mx-auto mb-2" />
                      <span className="text-xl font-mono">{formatTime(recordingTime)}</span>
                    </div>
                  ) : (
                    <Mic className="w-12 h-12 text-gray-400" />
                  )}
                </div>

                {isEvolucao && isRecording && (
                  <div
                    className={`max-w-sm mx-auto mb-4 px-4 py-2 rounded-lg font-mono text-sm font-semibold ${
                      remainingSeconds <= 30
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 animate-pulse'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                    }`}
                    data-testid="evolucao-countdown"
                  >
                    Tempo restante: {formatTime(remainingSeconds)} (máx 5 min)
                  </div>
                )}

                <div className="flex justify-center gap-4 flex-wrap">
                  {!isRecording ? (
                    <Button onClick={startRecording} size="lg" data-testid="start-recording">
                      <Mic className="w-5 h-5 mr-2" /> Iniciar Gravação
                    </Button>
                  ) : (
                    <Button onClick={stopRecording} variant="destructive" size="lg" data-testid="stop-recording">
                      <Square className="w-5 h-5 mr-2" /> Parar Gravação
                    </Button>
                  )}
                  <Button
                    onClick={cancelRecording}
                    variant="outline"
                    size="lg"
                    data-testid="cancel-recording"
                  >
                    <X className="w-5 h-5 mr-2" /> Cancelar
                  </Button>
                </div>
                
                <p className="text-sm text-gray-500 mt-4">
                  {consultationType === 'avaliacao' 
                    ? 'Tempo recomendado: até 30 minutos — Avaliação inicial completa.' 
                    : 'Duração máxima: 5 minutos — Registro rápido da sessão (a gravação para automaticamente ao atingir o limite).'}
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  {consultationType === 'avaliacao'
                    ? (templateType === 'pilates' 
                        ? 'Relate queixas, histórico de saúde, avaliação postural, objetivos e proposta inicial de trabalho.'
                        : 'Relate queixas, histórico, exame físico, objetivos e condutas iniciais.')
                    : (templateType === 'pilates'
                        ? 'Relate queixas iniciais, exercícios realizados, intercorrências e resposta do aluno.'
                        : 'Relate como o paciente chegou, procedimentos realizados, evolução clínica e conduta para próxima sessão.')}
                </p>
              </div>
            ) : (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="mb-4">Gravação concluída ({formatTime(recordingTime)})</p>
                
                {!transcription && (
                  <div className="flex justify-center gap-4">
                    <Button 
                      onClick={handleTranscribe} 
                      disabled={isTranscribing}
                      data-testid="transcribe-audio"
                    >
                      {isTranscribing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcrevendo...</>
                      ) : (
                        'Transcrever Áudio'
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => { setAudioBlob(null); setRecordingTime(0); }}
                    >
                      Gravar Novamente
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {transcription && (
              <div className="mt-6">
                <label className="block font-medium mb-2">Transcrição:</label>
                <Textarea 
                  value={transcription}
                  onChange={(e) => setTranscription(e.target.value)}
                  rows={8}
                  className="w-full"
                  data-testid="transcription-text"
                />
                <div className="flex justify-end gap-4 mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => { setAudioBlob(null); setTranscription(''); setRecordingTime(0); }}
                  >
                    Gravar Novamente
                  </Button>
                  <Button onClick={handleContinue} data-testid="continue-to-generate">
                    Continuar <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
            
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => setInputMode('type')}>
                Prefiro digitar manualmente
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {inputMode === 'type' && (
        <Card>
          <CardContent className="p-8">
            <label className="block font-medium mb-2">
              Digite as informações da consulta:
            </label>
            <Textarea 
              value={transcription}
              onChange={(e) => setTranscription(e.target.value)}
              rows={12}
              placeholder={templateType === 'pilates' 
                ? "Descreva os exercícios realizados, séries, repetições, equipamentos, observações sobre o aluno..."
                : "Descreva a queixa principal, histórico, avaliação física, diagnóstico, conduta aplicada..."}
              className="w-full"
              data-testid="manual-input"
            />
            <div className="flex justify-end gap-4 mt-4">
              <Button variant="outline" onClick={() => setInputMode(null)}>
                Voltar
              </Button>
              <Button 
                onClick={handleContinue} 
                disabled={!transcription.trim()}
                data-testid="continue-manual"
              >
                Continuar <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== STEP 6: AI PROCESSING ====================
const ProcessingStep = ({ consultationId, transcription, consultationType, templateType, onComplete }) => {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);
  const [emailGateInfo, setEmailGateInfo] = useState(null);

  const generateDoc = useCallback(async () => {
    setStatus('processing');
    setError(null);
    try {
      const result = await api.generateDocumentation(consultationId, {
        transcription,
        consultation_type: consultationType,
        template_type: templateType
      });

      if (result.success) {
        track('documentation_generated', { consultation_type: consultationType, template_type: templateType });
        onComplete(result.clinical_documentation || result.document_content);
      } else {
        throw new Error('Falha na geração');
      }
    } catch (err) {
      const httpStatus = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // AUTH-02 — email not verified → show blocking gate (NOT a toast)
      if (httpStatus === 403 && detail && typeof detail === 'object' && detail.error === 'email_nao_verificado') {
        setEmailGateInfo({ maskedEmail: detail.email });
        setStatus('email_gate');
        return;
      }
      // BILLING-02 — quota exhausted → show blocking screen instead of toast
      if (httpStatus === 403 && detail && typeof detail === 'object' && detail.error === 'limite_atingido') {
        setLimitInfo(detail);
        setStatus('limit_reached');
        return;
      }
      setError(typeof detail === 'string' ? detail : (err.message || 'Erro ao gerar documentação'));
      setStatus('error');
    }
  }, [consultationId, transcription, consultationType, templateType, onComplete]);

  useEffect(() => {
    generateDoc();
  }, [generateDoc]);

  if (status === 'email_gate') {
    return (
      <EmailVerificationGate
        maskedEmail={emailGateInfo?.maskedEmail}
        onRetry={generateDoc}
      />
    );
  }

  if (status === 'limit_reached') {
    return <LimitReachedScreen info={limitInfo || {}} />;
  }

  if (status === 'error') {
    return (
      <div className="max-w-xl mx-auto text-center">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">Erro ao processar</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto text-center">
      <div className="w-24 h-24 mx-auto mb-6 relative">
        <div
          className="absolute inset-0 border-4 rounded-full"
          style={{ borderColor: 'rgba(139, 101, 255, 0.25)' }}
        ></div>
        <div
          className="absolute inset-0 border-4 rounded-full border-t-transparent animate-spin"
          style={{ borderRightColor: '#8B65FF', borderBottomColor: '#8B65FF', borderLeftColor: '#8B65FF' }}
        ></div>
        <FileText
          className="w-10 h-10 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          style={{ color: '#8B65FF' }}
        />
      </div>
      <h3 className="text-xl font-bold mb-2">IA Processando...</h3>
      <p className="text-gray-500">
        Analisando transcrição e gerando documentação estruturada
      </p>
      <p className="text-sm text-gray-400 mt-4">
        Isso pode levar alguns segundos
      </p>
    </div>
  );
};

// ==================== STEP 7-8: REVIEW & APPROVAL ====================
const ReviewApprovalStep = ({ 
  document, 
  transcription, 
  consultationType, 
  templateType,
  onApprove,
  onBack,
  isEditing,
  setIsEditing,
  editedDocument,
  setEditedDocument
}) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [approvedSections, setApprovedSections] = useState({});

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const approveSection = (key) => {
    setApprovedSections(prev => ({ ...prev, [key]: true }));
  };

  const allSectionsApproved = () => {
    const docKeys = Object.keys(document || {}).filter(k => !k.startsWith('_'));
    return docKeys.length > 0 && docKeys.every(key => approvedSections[key]);
  };

  const renderSection = (key, value, depth = 0) => {
    if (key.startsWith('_')) return null;
    
    const isApproved = approvedSections[key];
    const isExpanded = expandedSections[key] !== false;
    
    const formatKey = (k) => {
      const labels = {
        // Top-level sections
        main_complaint: 'Queixa Principal',
        clinical_history: 'Histórico Clínico',
        physical_assessment: 'Avaliação Fisioterapêutica',
        functional_diagnosis: 'Diagnóstico Funcional',
        treatment_plan: 'Plano de Tratamento',
        session_evolution: 'Evolução da Sessão',
        anamnesis: 'Anamnese',
        physical_exam: 'Exame Físico',
        patient_guidelines: 'Orientações ao Paciente',
        session_complaint: 'Queixa da Sessão',
        exercises_performed: 'Exercícios Realizados',
        incidents_observations: 'Intercorrências',
        student_response: 'Resposta do Aluno',
        next_session_proposal: 'Próxima Sessão',
        // Treatment plan phase fields
        phase_number: 'Fase',
        duration: 'Duração',
        techniques: 'Técnicas',
        progression_criteria: 'Critérios de Progressão',
        // Patient guidelines fields
        home_exercises: 'Exercícios Domiciliares',
        warning_signs: 'Sinais de Alerta',
        recommendations: 'Recomendações',
        precautions: 'Precauções',
        lifestyle_advice: 'Orientações de Estilo de Vida',
        red_flags: 'Sinais de Alerta',
        // Physical exam / general fields
        eva_score: 'EVA',
        range_of_motion: 'ADM',
        functional_tests: 'Testes Funcionais',
        procedures_performed: 'Procedimentos',
        cif_diagnosis: 'Diagnóstico CIF',
        identified_problems: 'Problemas Identificados',
        prognosis: 'Prognóstico',
        objectives: 'Objetivos',
        interventions: 'Intervenções',
        progression: 'Progressão',
        frequency: 'Frequência',
        // Anamnesis subfields
        queixa_principal: 'Queixa Principal',
        historia_doenca_atual: 'História da Doença Atual',
        historia_pregressa: 'História Pregressa',
        antecedentes: 'Antecedentes',
        medicamentos: 'Medicamentos em Uso',
        habitos_de_vida: 'Hábitos de Vida',
        // Generic
        observations: 'Observações',
        notes: 'Anotações',
        // === Bug 3 additions — Anamnesis & common subsections ===
        patient_identification: 'Identificação do Paciente',
        current_history: 'Histórico Atual',
        medical_history: 'Histórico Médico',
        family_history: 'Histórico Familiar',
        social_history: 'Histórico Social',
        lifestyle: 'Estilo de Vida',
        chief_complaint: 'Queixa Principal',
        review_of_systems: 'Revisão de Sistemas',
        past_medical_history: 'Histórico Médico Pregresso',
        medications: 'Medicamentos em Uso',
        allergies: 'Alergias',
        pain_assessment: 'Avaliação da Dor',
        functional_limitations: 'Limitações Funcionais',
        patient_goals: 'Objetivos do Paciente',
        // === Comprehensive scan additions ===
        history_of_present_illness: 'História da Doença Atual',
        complaint_description: 'Descrição da Queixa',
        onset: 'Início',
        pain_intensity: 'Intensidade da Dor',
        pain_location: 'Localização da Dor',
        pain_quality: 'Característica da Dor',
        pain_aggravating_factors: 'Fatores que Agravam',
        pain_relieving_factors: 'Fatores que Aliviam',
        previous_treatments: 'Tratamentos Prévios',
        surgical_history: 'Histórico Cirúrgico',
        comorbidities: 'Comorbidades',
        exercise_habits: 'Hábitos de Exercício',
        sleep_quality: 'Qualidade do Sono',
        smoking: 'Tabagismo',
        alcohol: 'Consumo de Álcool',
        diet: 'Alimentação',
        occupation: 'Ocupação',
        marital_status: 'Estado Civil',
        // Physical exam additions
        inspection: 'Inspeção',
        palpation: 'Palpação',
        posture_assessment: 'Avaliação Postural',
        muscle_strength: 'Força Muscular',
        flexibility: 'Flexibilidade',
        balance: 'Equilíbrio',
        coordination: 'Coordenação',
        gait_analysis: 'Análise da Marcha',
        special_tests: 'Testes Especiais',
        neurological_exam: 'Exame Neurológico',
        cardiovascular_assessment: 'Avaliação Cardiovascular',
        respiratory_assessment: 'Avaliação Respiratória',
        // Diagnosis additions
        body_functions: 'Funções do Corpo',
        body_structures: 'Estruturas do Corpo',
        activities_and_participation: 'Atividades e Participação',
        environmental_factors: 'Fatores Ambientais',
        personal_factors: 'Fatores Pessoais',
        clinical_impression: 'Impressão Clínica',
        differential_diagnosis: 'Diagnóstico Diferencial',
        // Treatment / plan additions
        short_term_goals: 'Objetivos de Curto Prazo',
        long_term_goals: 'Objetivos de Longo Prazo',
        phases: 'Fases',
        modalities: 'Modalidades',
        exercises: 'Exercícios',
        manual_therapy: 'Terapia Manual',
        equipment: 'Equipamentos',
        sessions_per_week: 'Sessões por Semana',
        estimated_sessions: 'Sessões Estimadas',
        reassessment_criteria: 'Critérios de Reavaliação',
        discharge_criteria: 'Critérios de Alta',
        // Patient guidelines additions
        functional_goals: 'Metas Funcionais',
        self_care: 'Autocuidado',
        return_to_activity: 'Retorno à Atividade',
        ergonomics: 'Ergonomia',
        when_to_seek_help: 'Quando Procurar Ajuda',
        follow_up: 'Acompanhamento',
      };
      return labels[k] || k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Render a single nested object as labelled key/value pairs (used inside arrays of objects)
    const renderObjectFields = (obj, scopeKey) => (
      <div className="space-y-2 mt-1">
        {Object.entries(obj).map(([subKey, subVal]) => {
          if (subVal === null || subVal === undefined || subVal === '') return null;
          // Special-case phase_number → render as "Fase N" inline header instead of field
          if (subKey === 'phase_number') return null;
          if (Array.isArray(subVal)) {
            const items = subVal.filter((x) => x !== null && x !== undefined && x !== '');
            if (items.length === 0) return null;
            return (
              <div key={`${scopeKey}-${subKey}`}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {formatKey(subKey)}:
                </span>
                <ul className="list-disc list-inside ml-2 mt-0.5">
                  {items.map((item, i) => (
                    <li
                      key={`${scopeKey}-${subKey}-${i}-${String(typeof item === 'object' ? JSON.stringify(item) : item).slice(0, 30)}`}
                      className="text-sm"
                    >
                      {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          if (typeof subVal === 'object') {
            // Nested object — render its fields recursively (depth-2 max in practice)
            return (
              <div key={`${scopeKey}-${subKey}`}>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {formatKey(subKey)}:
                </span>
                {renderObjectFields(subVal, `${scopeKey}-${subKey}`)}
              </div>
            );
          }
          return (
            <div key={`${scopeKey}-${subKey}`}>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                {formatKey(subKey)}:
              </span>
              <span className="text-sm ml-2">{String(subVal)}</span>
            </div>
          );
        })}
      </div>
    );

    // Renders an approval card wrapper for top-level sections (depth === 0).
    // Used for both object-shaped sections (e.g., anamnesis) AND array-shaped sections
    // (e.g., treatment_plan), so EVERY top-level section gets an "Aprovar" button.
    const renderTopSectionWrapper = (children) => (
      <div
        key={key}
        className={`border rounded-lg mb-4 ${isApproved ? 'border-green-300 bg-green-50 dark:bg-green-900/20' : ''}`}
        data-testid={`review-section-${key}`}
      >
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
          onClick={() => toggleSection(key)}
        >
          <div className="flex items-center gap-2">
            {isApproved && <CheckCircle className="w-5 h-5 text-green-500" />}
            <h4 className="font-medium">{formatKey(key)}</h4>
          </div>
          <div className="flex items-center gap-2">
            {!isApproved && !isEditing && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); approveSection(key); }}
                data-testid={`approve-section-${key}-btn`}
              >
                <Check className="w-4 h-4 mr-1" /> Aprovar
              </Button>
            )}
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </div>
        {isExpanded && (
          <div className="p-4 pt-0 space-y-2">
            {children}
          </div>
        )}
      </div>
    );

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const inner = Object.entries(value).map(([k, v]) => renderSection(k, v, depth + 1));
      if (depth === 0) {
        return renderTopSectionWrapper(inner);
      }
      // Nested object (not top level) — render without approval card
      return (
        <div key={key} className="mb-2">
          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{formatKey(key)}</h5>
          <div className="ml-2 space-y-1">{inner}</div>
        </div>
      );
    }

    if (Array.isArray(value)) {
      // If array contains objects (e.g., treatment_plan phases), render each as a labelled card.
      const isObjectArray = value.some((it) => typeof it === 'object' && it !== null && !Array.isArray(it));
      const arrayBody = isObjectArray ? (
        <div className="mt-1 space-y-3">
          {value.map((item, i) => {
            if (item === null || item === undefined) return null;
            if (typeof item !== 'object') {
              return (
                <div key={`${key}-${i}`} className="text-sm pl-2">
                  • {String(item)}
                </div>
              );
            }
            // Build phase title: "Fase N – Duração" when treatment_plan-like
            const phaseNum = item.phase_number ?? i + 1;
            const dur = item.duration ? ` – ${item.duration}` : '';
            const titlePrefix = key === 'treatment_plan' ? `Fase ${phaseNum}${dur}` : null;
            return (
              <div
                key={`${key}-${i}-${(item.phase_number || item.id || i)}`}
                className="border-l-2 border-primary/40 pl-3 py-1"
              >
                {titlePrefix && (
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    {titlePrefix}
                  </div>
                )}
                {renderObjectFields(item, `${key}-${i}`)}
              </div>
            );
          })}
        </div>
      ) : (
        <ul className="list-disc list-inside ml-2 mt-1">
          {value.map((item, i) => {
            const label = typeof item === 'object' ? JSON.stringify(item) : String(item);
            return (
              <li key={`${key}-${i}-${label.slice(0, 30)}`} className="text-sm">
                {label}
              </li>
            );
          })}
        </ul>
      );

      if (depth === 0) {
        // Wrap in approval card so EVERY top-level section (including treatment_plan) has "Aprovar"
        return renderTopSectionWrapper(arrayBody);
      }
      // Nested array — render inline with label
      return (
        <div key={key} className="mb-3">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{formatKey(key)}:</span>
          {arrayBody}
        </div>
      );
    }

    return (
      <div key={key} className="mb-3">
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{formatKey(key)}:</span>
        {isEditing ? (
          <Textarea 
            value={editedDocument?.[key] || value || ''}
            onChange={(e) => setEditedDocument(prev => ({ ...prev, [key]: e.target.value }))}
            className="mt-1"
            rows={2}
          />
        ) : (
          <p className="text-sm mt-1">{value || 'Não informado'}</p>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: Transcription */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Transcrição Original</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg max-h-[600px] overflow-y-auto">
              <p className="whitespace-pre-wrap text-sm">{transcription}</p>
            </div>
          </CardContent>
        </Card>

        {/* Right: Document */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Documentação Gerada</CardTitle>
              <CardDescription>Revise e aprove cada seção</CardDescription>
            </div>
            <div className="flex gap-2">
              {!isEditing ? (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-1" /> Editar
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                  <Button size="sm" onClick={() => { /* Save logic */ setIsEditing(false); }}>
                    <Save className="w-4 h-4 mr-1" /> Salvar
                  </Button>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto">
            {document && Object.entries(document).map(([key, value]) => renderSection(key, value))}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Actions */}
      <div className="flex justify-between items-center mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {Object.keys(approvedSections).filter(k => approvedSections[k]).length} de {Object.keys(document || {}).filter(k => !k.startsWith('_')).length} seções aprovadas
          </span>
          <Button 
            onClick={onApprove}
            disabled={!allSectionsApproved()}
            data-testid="save-document"
          >
            <CheckCircle className="w-4 h-4 mr-2" /> Salvar Prontuário
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const ConsultationFlowPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [patient, setPatient] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [hasApprovedEvaluation, setHasApprovedEvaluation] = useState(false);
  const [approvedEvaluationDate, setApprovedEvaluationDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Flow state — starts at step 2 (Especialidade) per new spec
  const [currentStep, setCurrentStep] = useState(2);
  const [consultationType, setConsultationType] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [document, setDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDocument, setEditedDocument] = useState(null);

  const { setGuard, clearGuard } = useUnsavedGuard();
  const { user } = useAuth();

  // Funnel events: normalize consultation_type per analytics spec
  // (codebase: "avaliacao"/"evolucao" → analytics: "anamnese"/"evolution").
  const normalizeType = (t) => (t === 'avaliacao' ? 'anamnese' : t === 'evolucao' ? 'evolution' : t);
  const funnelProps = (extra = {}) => ({
    user_id: user?.id || null,
    consultation_type: normalizeType(consultationType),
    timestamp: new Date().toISOString(),
    ...extra,
  });

  // AUTH-01 unsaved guard: while a consultation exists and is not yet
  // approved/exported, register a logout-time prompt so the user can save the
  // current draft (with whatever doc was generated so far) before leaving.
  useEffect(() => {
    if (!consultation) {
      clearGuard();
      return undefined;
    }
    const status = consultation?.status;
    if (status === 'approved' || status === 'exported') {
      clearGuard();
      return undefined;
    }
    setGuard({
      onSaveDraft: async () => {
        try {
          await api.saveDraft(consultation.id, editedDocument || document || null);
        } catch (err) {
          throw err;
        }
      },
    });
    return () => clearGuard();
  }, [consultation, document, editedDocument, setGuard, clearGuard]);

  // Load patient and check for approved evaluation
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load patient
        const patientData = await api.getPatient(patientId);
        setPatient(patientData);
        
        // Check for approved evaluation
        // Check if patient has an approved evaluation (critical for the alert logic)
        try {
          const evalCheck = await api.checkPatientHasEvaluation(patientId);
          setHasApprovedEvaluation(Boolean(evalCheck?.has_approved_evaluation));
          setApprovedEvaluationDate(evalCheck?.evaluation_date || null);
        } catch (_) {
          setHasApprovedEvaluation(false);
          setApprovedEvaluationDate(null);
        }
        
        // Check if resuming existing consultation
        const consultationId = searchParams.get('consultation');
        if (consultationId) {
          const existingConsultation = await api.getConsultation(consultationId);
          setConsultation(existingConsultation);
          setConsultationType(existingConsultation.consultation_type);
          setTemplateType(existingConsultation.template_type);
          setTranscription(existingConsultation.transcription || '');
          setDocument(existingConsultation.clinical_documentation);
          
          // Resume at appropriate step
          if (existingConsultation.clinical_documentation) {
            setCurrentStep(7);
          } else if (existingConsultation.transcription) {
            setCurrentStep(6);
          } else {
            // Fully-configured consultation, go straight to recording
            setCurrentStep(5);
          }
        }
        
      } catch (err) {
        setError(err.response?.data?.detail || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [patientId, searchParams]);

  // Step handlers
  const handleSpecialtySelect = (specialty) => {
    setTemplateType(specialty);
    setCurrentStep(3);
  };

  const handleConsultationTypeSelect = async (type) => {
    setConsultationType(type);
    // Create the consultation now that we have specialty + type
    try {
      const newConsultation = await api.createConsultation({
        patient_id: patientId,
        consultation_type: type,
        template_type: templateType
      });
      setConsultation(newConsultation);
      track('consultation_created', { consultation_type: type, template_type: templateType });
      // Funnel event #1 — session_started (analytics spec)
      track('session_started', {
        user_id: user?.id || null,
        consultation_type: normalizeType(type),
        timestamp: new Date().toISOString(),
      });
      setCurrentStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar consulta');
    }
  };

  const handleTranscriptionComplete = (text) => {
    setTranscription(text);
    setCurrentStep(6);
  };

  const handleDocumentGenerated = (doc) => {
    setDocument(doc);
    setEditedDocument(doc);
    setCurrentStep(7);
  };

  const handleApproveAndSave = async () => {
    try {
      // Update consultation to approved status
      await api.updateConsultation(consultation.id, {
        status: 'approved',
        clinical_documentation: editedDocument || document
      });
      
      // Navigate to success or patient page
      navigate(`/patients/${patientId}?success=true`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500">{error}</p>
          <Button onClick={() => navigate('/patients')} className="mt-4">
            Voltar para Pacientes
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="py-6">
        {/* Progress Bar */}
        <StepProgressBar 
          currentStep={currentStep} 
          consultationType={consultationType}
          templateType={templateType}
        />

        {/* Step Content */}
        {currentStep === 2 && (
          <SpecialtySelection
            patient={patient}
            onSelect={handleSpecialtySelect}
          />
        )}

        {currentStep === 3 && (
          <ConsultationTypeSelection 
            patient={patient}
            hasApprovedEvaluation={hasApprovedEvaluation}
            approvedEvaluationDate={approvedEvaluationDate}
            onSelect={handleConsultationTypeSelect}
            onBack={() => setCurrentStep(2)}
          />
        )}

        {currentStep === 5 && (
          <RecordingStep 
            consultationType={consultationType}
            templateType={templateType}
            onTranscriptionComplete={handleTranscriptionComplete}
            onBack={() => setCurrentStep(3)}
            onFunnelEvent={(name, extra) => track(name, funnelProps(extra))}
          />
        )}

        {currentStep === 6 && consultation && (
          <ProcessingStep 
            consultationId={consultation.id}
            transcription={transcription}
            consultationType={consultationType}
            templateType={templateType}
            onComplete={handleDocumentGenerated}
          />
        )}

        {currentStep >= 7 && document && (
          <ReviewApprovalStep 
            document={document}
            transcription={transcription}
            consultationType={consultationType}
            templateType={templateType}
            onApprove={handleApproveAndSave}
            onBack={() => setCurrentStep(5)}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            editedDocument={editedDocument}
            setEditedDocument={setEditedDocument}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ConsultationFlowPage;
